import type { ReactNode } from 'react';
import { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import {
  createMomentumController,
  detectCurrentSlide,
  scrollToSlide,
  createDragHandlers,
  createNavigationHandlers,
  createGlobalEventHandlers,
  X_PADDING,
  type DragState,
  type MomentumController,
} from './utils';

interface TouchSliderProps {
  children: ReactNode[];
  onSlideChange?: (currentIndex: number) => void;
  showButtons?: boolean;
  initialSlide: number;
}

const DRAG_STATE_INITIAL: DragState = {
  isDragging: false,
  startX: 0,
  scrollLeft: 0,
  lastMoveX: 0,
  lastMoveTime: 0,
  velocity: 0,
};

export const TouchSlider = ({
  children,
  onSlideChange,
  showButtons = true,
  initialSlide = 0,
}: TouchSliderProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const dragState = useRef(DRAG_STATE_INITIAL);
  const momentumRef = useRef<number | null>(null);
  const momentumController = useRef<MomentumController>(
    createMomentumController()
  );
  const dragStarted = useRef(false);
  const [currentSlide, setCurrentSlide] = useState(initialSlide);

  const updateCurrentSlideFromScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const result = detectCurrentSlide(container, children.length);
    if (!result.shouldUpdate) return;

    setCurrentSlide((prev) => {
      if (result.currentIndex !== prev) {
        return result.currentIndex;
      }
      return prev;
    });
  }, [children.length]);

  // Handle onSlideChange callback when currentSlide changes (but not on initial mount)
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    onSlideChange?.(currentSlide);
  }, [currentSlide, onSlideChange]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('scroll', updateCurrentSlideFromScroll);

    return () => {
      container.removeEventListener('scroll', updateCurrentSlideFromScroll);
    };
  }, [updateCurrentSlideFromScroll]);

  const scrollToSlideCallback = useCallback((index: number) => {
    const container = containerRef.current;
    if (!container) return;
    scrollToSlide(container, index);
  }, []);

  // Scroll to initial slide on mount
  useEffect(() => {
    scrollToSlideCallback(initialSlide);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only runs once on mount
  }, []);

  const navigationHandlers = useMemo(
    () =>
      createNavigationHandlers(
        currentSlide,
        children.length,
        scrollToSlideCallback,
        momentumController,
        momentumRef
      ),
    [currentSlide, children.length, scrollToSlideCallback]
  );

  const dragHandlers = useMemo(
    () =>
      createDragHandlers(
        containerRef,
        dragState,
        momentumController,
        momentumRef,
        dragStarted
      ),
    []
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      dragHandlers.handlePointerStart(e.pageX);
      e.preventDefault();
    },
    [dragHandlers]
  );

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      dragHandlers.handlePointerStart(e.touches[0].pageX);
    },
    [dragHandlers]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      dragHandlers.handlePointerMove(e.touches[0].pageX);
    },
    [dragHandlers]
  );

  const handleTouchEnd = useCallback(() => {
    dragHandlers.handlePointerEnd();
  }, [dragHandlers]);

  useEffect(() => {
    const globalEventHandlers = createGlobalEventHandlers(
      dragHandlers.handlePointerMove,
      dragHandlers.handlePointerEnd,
      dragStarted,
      momentumController,
      momentumRef
    );

    const container = containerRef.current;
    if (container) {
      container.addEventListener(
        'mousedown',
        globalEventHandlers.handleMouseDownCapture
      );
      return () => {
        container.removeEventListener(
          'mousedown',
          globalEventHandlers.handleMouseDownCapture
        );
        globalEventHandlers.cleanup();
      };
    }
  }, [dragHandlers]);

  return (
    <div className="group relative grid [-webkit-tap-highlight-color:transparent]">
      {showButtons && (
        <>
          <button
            onClick={navigationHandlers.handlePrevious}
            disabled={currentSlide === 0}
            className="group/btn absolute inset-y-0 start-0 z-10 grid items-center justify-start bg-gradient-to-r from-[--s-color-surface-2] to-transparent pl-1 transition-opacity duration-200 disabled:pointer-events-none"
            style={{ width: `${X_PADDING}px` }}
            aria-label="Previous slide"
          >
            <svg
              className="size-4 group-disabled/btn:opacity-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>

          <button
            onClick={navigationHandlers.handleNext}
            disabled={currentSlide === children.length - 1}
            className="group/btn absolute inset-y-0 end-0 z-10 grid items-center justify-end bg-gradient-to-l from-[--s-color-surface-2] to-transparent pr-1 transition-opacity duration-200 disabled:pointer-events-none disabled:opacity-0"
            style={{ width: `${X_PADDING}px` }}
            aria-label="Next slide"
          >
            <svg
              className="size-4 group-disabled/btn:opacity-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </>
      )}

      <div
        ref={containerRef}
        className="flex cursor-grab touch-pan-y touch-pinch-zoom select-none overflow-x-hidden"
        style={{ paddingInline: `${X_PADDING}px` }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        role="list"
        aria-live="polite"
      >
        {children.map((child, index) => (
          <div
            key={index}
            role="listitem"
            data-slider-item
            data-active={currentSlide === index}
            data-index={index}
            className="shrink-0"
          >
            {child}
          </div>
        ))}
      </div>

      <div className="sr-only">Drag to scroll.</div>
    </div>
  );
};
